import {
  Box,
  Chip,
  Link,
  List,
  ListItem,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { RefObject, useEffect, useState } from 'react';
import { DETECTED } from '../../../../constants/report';
import YourNasemScaleWithGuidelines from '../../../../reports/YourNasemScaleWithGuidelines';
import {
  primaryBulletStyle,
  primaryListItemStyle,
  secondaryBulletStyle,
  secondaryListItemStyle,
} from '../../../../styles/HowToUnderstandPFASStyles';
import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';
import { useLatestReport } from '../../../../utils/reports';
import GradientCircularProgress from '../../../common/GradientCircularProgress';

type WhatYourResultsMeanProps = {
  totalPfasRef?: RefObject<HTMLDivElement>;
};

const WhatYourResultsMean = ({ totalPfasRef }: WhatYourResultsMeanProps) => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isResultDetailsExpanded, setIsResultDetailsExpanded] = useState(false);

  const [reportData, setReportData] = useState<any>();
  const { latestReportLoading, fetchLatestReport } = useLatestReport();

  useEffect(() => {
    const fetchLatestReportsFunction = async () => {
      let latestReportResponse = await fetchLatestReport();
      if (!latestReportResponse.message && latestReportResponse.latestReport) {
        setReportData(latestReportResponse.latestReport);
      }
    };
    fetchLatestReportsFunction();
  }, [fetchLatestReport]);
  const isReportReady = !!reportData && !latestReportLoading;

  const boxesStyles = {
    background: theme.custom.pages.howTo.whatYourResultsMean.summaryStatistics.boxStyles.backgroundColor,
    width: { xs: '100%', sm: '48%', md: '32%' },
    padding: '24px',
    borderRadius: '16px',
  };

  const handleResultDetailsExpandToggle = () => {
    setIsResultDetailsExpanded((prev) => !prev);
  };

  const summaryStatistics = [
    {
      title: 'Number of PFAS detected',
      content:
        'This tells you how many of the 15 PFAS compounds we tested were found in your blood. This includes compounds where we only detected a very low level.',
    },
    {
      title: 'Total PFAS detected',
      content:
        'This is a sum of the levels of all the PFAS we detected and measured in your sample. Note that this excludes compounds that were detected at very low levels. Compounds with very high levels may be capped at 50. This result is given in ng/mL of blood.',
      isTarget: true, // if isTarget is true, attach ref.
    },
    {
      title: 'NASEM Level',
      content:
        'This is based on guidelines from the National Academies of Science, Engineering, and Medicine (NASEM). It is calculated by summing levels of certain PFAS in your result. This sum is multiplied by 2 to give a PFAS score in ng/mL of serum. This is compared to the NASEM guidelines shown below.',
    },
  ];

  const renderSummaryStatistics = (totalPfasRef: React.RefObject<HTMLDivElement> | undefined) => {
    return (
      <Box
        sx={{
          marginBottom: '24px',
          textAlign: 'left',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.whatYourResultsMean.renderSummaryStatistics.typography.body1.color,
          }}
        >
          Summary statistics
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: theme.custom.pages.howTo.whatYourResultsMean.renderSummaryStatistics.typography.body2.color,
            marginBottom: '16px',
          }}
        >
          Your in-app report includes 3 important summary statistics:
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2, md: 2 }}
          sx={{
            justifyContent: 'space-between',
          }}
        >
          {summaryStatistics.map((item, index) => (
            <Box key={index} sx={boxesStyles} ref={item.isTarget ? totalPfasRef : null}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '18px',
                  color: theme.custom.pages.howTo.whatYourResultsMean.summaryStatistics.typography.body1.color,
                  fontWeight: 400,
                  marginBottom: '16px',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  color: theme.custom.pages.howTo.whatYourResultsMean.summaryStatistics.typography.body2.color,
                  fontWeight: 400,
                }}
              >
                {item.content}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderAboutTestAndMedicalEffects = () => {
    const aboutTestAndMedicalEffects = [
      {
        title: 'About the test',
        content:
          'The test uses a well-established scientific analysis method to identify and measure PFAS. It combines high accuracy liquid chromatography (LC) coupled with high resolution mass spectrometry (MS). Our test is a whole blood test. This is different from traditional tests which measure blood serum PFAS (serum is the liquid part of the blood once all proteins and blood cells have been removed).',
      },
      {
        title: 'What are the medical effects of PFAS?',
        content: (
          <>
            Research into the effects of PFAS exposure is ongoing. PFAS exposure is linked to testicular, kidney, and
            pancreatic cancer. Two PFAS compounds, PFOA and PFOS, are classified as carcinogenic by the &nbsp;
            <a
              rel="noreferrer"
              style={{
                color:
                  theme.custom.pages.howTo.whatYourResultsMean.renderAboutTestAndMedicalEffects.typography.link.color,
              }}
              target="_blank"
              href="https://www.iarc.who.int"
            >
              International Agency for Research on Cancer (IARC)
            </a>
            . Many other studies link PFAS with a range of health conditions, including cardiovascular disease,
            immunosuppression, and reduced birth weight.
          </>
        ),
      },
    ];

    return (
      <Box sx={{ marginTop: '24px', textAlign: 'left' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          gap="16px"
          sx={{
            mb: 1,
            justifyContent: 'space-between',
            alignItems: 'stretch',
          }}
        >
          {aboutTestAndMedicalEffects.map((item, index) => (
            <Box
              key={index}
              sx={{
                background:
                  theme.custom.pages.howTo.whatYourResultsMean.renderAboutTestAndMedicalEffects.boxStyles
                    .backgroundColor,
                padding: '24px',
                borderRadius: '24px',
                width: { xs: '100%', sm: '50%' },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '18px',
                  color:
                    theme.custom.pages.howTo.whatYourResultsMean.renderAboutTestAndMedicalEffects.typography.body1
                      .color,
                  fontWeight: 400,
                  marginBottom: '16px',
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '12px',
                  color:
                    theme.custom.pages.howTo.whatYourResultsMean.renderAboutTestAndMedicalEffects.typography.body2
                      .color,
                  fontWeight: 400,
                }}
              >
                {item.content}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderTableDetailsListItems = () => {
    return (
      <Box sx={{ pl: 2, marginBottom: '32px' }}>
        <List dense sx={{ listStyleType: 'disc' }}>
          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Short name:{' '}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              This is the abbreviation for the specific PFAS compound.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Full name:{' '}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              This is the full chemical name of of the PFAS compound.
            </Typography>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Current level:{' '}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              This is the level of the compound that was measured in your blood given in nanograms per milliliter
              (ng/mL). This row may also show one of the following: This row may also show one of the following:
            </Typography>
            <Box sx={{ paddingLeft: '40px', paddingRight: '40px' }}>
              <List dense sx={{ listStyleType: 'disc', padding: '0px' }}>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={primaryListItemStyle}>
                    Not Detected.{' '}
                  </Typography>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    The compound was either absent, or at such low concentration we couldn't detect it in your sample. 
                  </Typography>
                </ListItem>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={primaryListItemStyle}>
                    Below Reportable Range.{' '}
                  </Typography>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    The compound is at such a low concentration that it lands below our Reporting Limit (see below).
                  </Typography>
                </ListItem>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={primaryListItemStyle}>
                    Above Reportable Range.{' '}
                  </Typography>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    The compound is at such a high concentration that is above our Upper Reporting Limit. At present,
                    this is set to 50 ng/mL although we will try to report levels up to 100 ng/mL.
                  </Typography>
                </ListItem>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={primaryListItemStyle}>
                    Inconclusive.{' '}
                  </Typography>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    A final result was unable to be determined. This can be caused by many factors during the sample
                    collection or testing process.
                  </Typography>
                </ListItem>
              </List>
            </Box>
          </ListItem>
          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Lower Reporting Limit:{' '}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              Your test result includes a column marked “RL”. This stands for Lower Reporting Limit. This is the lowest
              amount of the substance that our equipment can reliably detect and accurately measure. This means:
            </Typography>
            <Box sx={{ paddingLeft: '40px', paddingRight: '40px' }}>
              <List dense sx={{ listStyleType: 'disc', padding: '0px' }}>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    We are certain the compound is actually there.
                  </Typography>
                </ListItem>
                <ListItem disableGutters sx={{ ...secondaryBulletStyle, display: 'list-item' }}>
                  <Typography component="span" sx={secondaryListItemStyle}>
                    Our validation tests prove we can precisely measure the substance at this level.
                  </Typography>
                </ListItem>
              </List>
            </Box>
          </ListItem>

          <ListItem disableGutters sx={primaryBulletStyle}>
            <Typography component="span" sx={primaryListItemStyle}>
              Detection Frequency:{' '}
            </Typography>
            <Typography component="span" sx={secondaryListItemStyle}>
              How often this compound has been detected in capillary blood in this{' '}
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href="https://pubs.acs.org/doi/10.1021/acs.est.2c09852"
                sx={{
                  fontWeight: 400,
                  fontSize: '14px',
                  color: theme.custom.pages.howTo.whatYourResultsMean.renderResultTableDetails.typography.link.color,
                  textDecorationLine: 'underline',
                  textDecorationStyle: 'solid',
                  textDecorationSkipInk: 'auto',
                  textDecorationThickness: 'auto',
                  textUnderlineOffset: 'auto',
                  textUnderlinePosition: 'from-font',
                }}
              >
                peer-reviewed scientific paper.
              </Link>
            </Typography>
          </ListItem>
        </List>
      </Box>
    );
  };

  const renderSingleTableHeaderAndRow = () => {
    const StyledTableContainer = styled(TableContainer)({
      boxShadow: 'none',
      borderTopRightRadius: '12px',
      borderTopLeftRadius: '12px',
      borderBottomRightRadius: '12px',
      borderBottomLeftRadius: '12px',
      backgroundColor:
        theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledTableContainer.backgroundColor,
    });

    const StyledTableRow = styled(TableRow)({
      backgroundColor:
        theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledTableRow.backgroundColor,
      '&.MuiTableRow-hover:hover': {
        cursor: 'pointer',
        backgroundColor:
          theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledTableRow.hover
            .backgroundColor,
      },
    });

    const StyledHeaderCell = styled(TableCell)({
      color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledHeaderCell.color,
      fontWeight: 500,
      fontSize: '16px',
      borderBottom: `1px solid ${theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledHeaderCell.borderBottom}`,
      backgroundColor:
        theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledHeaderCell.backgroundColor,
      padding: '12px 16px',
    });

    const StyledShortNameCell = styled(TableCell)({
      color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledShortNameCell.color,
      fontSize: '14px',
      fontWeight: 500,
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledShortNameCell.borderBottom}`,
    });

    const StyledRegularCell = styled(TableCell)({
      color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledRegularCell.color,
      fontSize: '14px',
      fontWeight: 500,
      padding: '8px 24px',
      borderBottom: `1px solid ${theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledRegularCell.borderBottom}`,
    });

    const headerSubtextStyle = {
      color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.headerSubtextStyle.color,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
    };

    return (
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>Name</StyledHeaderCell>
              <StyledHeaderCell>
                Level{' '}
                <Typography component="span" sx={headerSubtextStyle}>
                  (ng/mL)
                </Typography>
              </StyledHeaderCell>
              <StyledHeaderCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography component="span">NHANES</Typography>
                  <Box
                    component="img"
                    src={theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.nhanesIcon}
                    alt="Sort Icon"
                    sx={{ width: '16px', height: '16px', marginLeft: '4px' }}
                  />
                </Box>
              </StyledHeaderCell>
              <StyledHeaderCell>
                LOQ{' '}
                <Typography component="span" sx={headerSubtextStyle}>
                  (ng/mL)
                </Typography>
              </StyledHeaderCell>
              <StyledHeaderCell>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography component="span">Detection freq</Typography>
                  <Box
                    component="img"
                    src={theme.custom.components.toolTipFromDB.images.src}
                    alt="Sort Icon"
                    sx={{ width: '16px', height: '16px', marginLeft: '4px' }}
                  />
                </Box>
              </StyledHeaderCell>
              <StyledHeaderCell>Detected</StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <StyledTableRow>
              <StyledShortNameCell>
                <Typography sx={{ fontWeight: '500' }}>PFBA</Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.styledName.color,
                  }}
                >
                  Perfluorobutanoic Acid
                </Typography>
              </StyledShortNameCell>
              <StyledRegularCell>2.8</StyledRegularCell>
              <StyledRegularCell>No</StyledRegularCell>
              <StyledRegularCell>0.5</StyledRegularCell>
              <StyledRegularCell>NA</StyledRegularCell>
              <StyledRegularCell>
                <Chip
                  label={
                    <div
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      {DETECTED}
                    </div>
                  }
                  sx={{
                    backgroundColor:
                      theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.chip.backgroundColor,
                    color: theme.custom.pages.howTo.whatYourResultsMean.renderSingleTableHeaderAndRow.chip.color,
                    minWidth: 0,
                    maxWidth: '114px',
                    height: '21px',
                    fontWeight: 400,
                    fontSize: '12px',
                    borderRadius: '16px',
                    display: 'inline-flex',
                    padding: '2px 8px 2px 6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'default',
                  }}
                />
              </StyledRegularCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </StyledTableContainer>
    );
  };

  const renderResultTableDetails = (isMobile: boolean) => {
    return (
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 400,
            fontSize: '18px',
            lineHeight: '28px',
            color: theme.custom.pages.howTo.whatYourResultsMean.renderResultTableDetails.typography.body1.color,
            marginTop: '32px',
          }}
        >
          More about the detailed results table
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            color: theme.custom.pages.howTo.whatYourResultsMean.renderResultTableDetails.typography.body2.color,
            marginBottom: '32px',
          }}
        >
          Your results include a table with a number of columns. These are as follows:
        </Typography>

        {renderTableDetailsListItems()}
        {!isMobile && renderSingleTableHeaderAndRow()}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderBottomRightRadius: '16px',
          borderBottomLeftRadius: '16px',
          padding: { xs: '24px 16px', sm: '24px 40px 40px 40px' },
          backgroundColor: theme.custom.pages.howTo.whatYourResultsMean.backgroundColor,
        }}
      >
        {renderSummaryStatistics(totalPfasRef)}
        {!isReportReady ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <GradientCircularProgress loadingMessage="Loading ..." />
          </Box>
        ) : (
          <YourNasemScaleWithGuidelines
            nasemLevel="low"
            displayNasemLevel={false}
            onToggle={handleResultDetailsExpandToggle}
            isExpanded={isResultDetailsExpanded}
            addPadding={false}
            testId={reportData?.test_id}
          />
        )}
        {renderAboutTestAndMedicalEffects()}
      </Box>
      <Box
        sx={{
          marginTop: '24px',
          width: '100%',
          padding: { xs: '24px 16px', sm: '24px 40px 40px 40px' },
          borderRadius: '16px',
          backgroundColor: theme.custom.pages.howTo.whatYourResultsMean.backgroundColor,
        }}
      >
        {renderResultTableDetails(isMobile)}
      </Box>
    </>
  );
};

export default WhatYourResultsMean;
